import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getPolicyDetail } from '../../../services'
import { Policy, PolicyDetail } from '../../../types'
import { Loader2 } from 'lucide-react'
import GroupEmployeesTable from './group-employees-table'

/** Safely parse a JSON string, return fallback on failure */
function safeJsonParse<T>(value: unknown, fallback: T): T {
    if (typeof value !== 'string') return fallback
    try {
        return JSON.parse(value) as T
    } catch {
        return fallback
    }
}

/** Parse config fields that Keycloak stores as stringified JSON */
function parseConfig(config?: Record<string, any>) {
    if (!config) return null

    const groups = safeJsonParse<{ id: string; extendChildren: boolean }[]>(
        config.groups,
        []
    )
    const roles = safeJsonParse<{ id: string; required: boolean }[]>(
        config.roles,
        []
    )
    const users = safeJsonParse<string[]>(config.users, [])

    // Collect remaining config keys that aren't parsed above
    const knownKeys = new Set(['groups', 'roles', 'users'])
    const otherEntries = Object.entries(config).filter(
        ([key]) => !knownKeys.has(key)
    )

    return { groups, roles, users, otherEntries }
}

// Renders roles/users/groups detail for a policy
const ExpandedPolicyDetail: React.FC<{ row: Policy }> = ({ row }) => {
    const { t } = useTranslation('authorization')

    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.AUTHORIZATION_POLICIES, 'detail', row.id],
        queryFn: () => getPolicyDetail(row.id),
    })

    const detail = data?.result as PolicyDetail | undefined

    const parsedConfig = useMemo(
        () => parseConfig(detail?.config),
        [detail?.config]
    )

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 p-4 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('translation:loading')}</span>
            </div>
        )
    }

    if (!detail) {
        return (
            <div className="p-4 text-sm text-muted-foreground">
                {t('translation:noData', 'Không có dữ liệu')}
            </div>
        )
    }

    // Use resolved groups/roles/users from API, fallback to parsed config
    const groups = detail.groups?.length
        ? detail.groups
        : parsedConfig?.groups || []
    const roles = detail.roles?.length
        ? detail.roles
        : parsedConfig?.roles || []
    const users = detail.users?.length
        ? detail.users
        : parsedConfig?.users || []

    return (
        <div className="p-4 space-y-3">
            {/* Description */}
            {detail.description && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Mô tả
                    </span>
                    <p className="text-sm">{detail.description}</p>
                </div>
            )}

            {/* Roles */}
            {roles.length > 0 && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Roles ({roles.length})
                    </span>
                    <table className="w-full mt-1 text-xs border-collapse">
                        <thead>
                            <tr className="bg-muted/50">
                                <th className="text-left p-2 border font-medium">
                                    ID
                                </th>
                                <th className="text-left p-2 border font-medium w-28">
                                    Required
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map((role) => (
                                <tr key={role.id}>
                                    <td className="p-2 border font-mono">
                                        {role.id}
                                    </td>
                                    <td className="p-2 border">
                                        {role.required ? (
                                            <span className="text-red-600">
                                                Yes
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">
                                                No
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Users — show userDetails if available */}
            {users.length > 0 && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Users ({users.length})
                    </span>
                    {detail.userDetails && detail.userDetails.length > 0 ? (
                        <table className="w-full mt-1 text-xs border-collapse">
                            <thead>
                                <tr className="bg-muted/50">
                                    <th className="text-left p-2 border font-medium">
                                        Username
                                    </th>
                                    <th className="text-left p-2 border font-medium">
                                        Họ tên
                                    </th>
                                    <th className="text-left p-2 border font-medium">
                                        Email
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.userDetails.map((user) => (
                                    <tr key={user.id}>
                                        <td className="p-2 border">
                                            {user.username}
                                        </td>
                                        <td className="p-2 border">
                                            {[user.firstName, user.lastName]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </td>
                                        <td className="p-2 border text-muted-foreground">
                                            {user.email || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="w-full mt-1 text-xs border-collapse">
                            <thead>
                                <tr className="bg-muted/50">
                                    <th className="text-left p-2 border font-medium">
                                        User ID
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((userId) => (
                                    <tr key={userId}>
                                        <td className="p-2 border font-mono">
                                            {userId}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Groups — use groupDetails (has name) as primary, fallback to raw groups */}
            {(detail.groupDetails?.length || groups.length > 0) && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Groups ({detail.groupDetails?.length || groups.length})
                    </span>
                    <div className="space-y-3 mt-1">
                        {detail.groupDetails && detail.groupDetails.length > 0
                            ? detail.groupDetails.map((group) => {
                                  const rawGroup = groups.find(
                                      (g) => g.id === group.id
                                  )
                                  return (
                                      <div
                                          key={group.id}
                                          className="border rounded-md p-3 bg-muted/30"
                                      >
                                          <div className="flex items-center gap-2 mb-2">
                                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200 font-medium">
                                                  {group.name}
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                  {group.path}
                                              </span>
                                              {(rawGroup?.extendChildren ||
                                                  group.extendChildren) && (
                                                  <span className="text-xs text-orange-600">
                                                      (+children)
                                                  </span>
                                              )}
                                          </div>
                                          <GroupEmployeesTable
                                              groupId={group.id}
                                          />
                                      </div>
                                  )
                              })
                            : groups.map((group) => (
                                  <div
                                      key={group.id}
                                      className="border rounded-md p-3 bg-muted/30"
                                  >
                                      <div className="flex items-center gap-2 mb-2">
                                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-purple-50 text-purple-700 border border-purple-200 font-medium">
                                              {group.id}
                                          </span>
                                          {group.extendChildren && (
                                              <span className="text-xs text-orange-600">
                                                  (+children)
                                              </span>
                                          )}
                                      </div>
                                      <GroupEmployeesTable groupId={group.id} />
                                  </div>
                              ))}
                    </div>
                </div>
            )}

            {/* Remaining config entries */}
            {parsedConfig?.otherEntries &&
                parsedConfig.otherEntries.length > 0 && (
                    <div>
                        <span className="text-xs font-semibold text-muted-foreground">
                            Config
                        </span>
                        <table className="w-full mt-1 text-xs border-collapse">
                            <thead>
                                <tr className="bg-muted/50">
                                    <th className="text-left p-2 border font-medium w-40">
                                        Key
                                    </th>
                                    <th className="text-left p-2 border font-medium">
                                        Value
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedConfig.otherEntries.map(
                                    ([key, value]) => (
                                        <tr key={key}>
                                            <td className="p-2 border font-mono">
                                                {key}
                                            </td>
                                            <td className="p-2 border">
                                                {typeof value === 'string'
                                                    ? value
                                                    : JSON.stringify(value)}
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
        </div>
    )
}

export default ExpandedPolicyDetail
